import { NotificationType } from 'common/enums';
import { appActions, AppContext } from 'context';
import { DropboxFile } from 'helpers/dropbox';
import { fetchDropboxFile, planDropboxImport } from 'helpers/dropbox-import';
import { DriveFile } from 'helpers/google-drive';
import { fetchDriveFile, planDriveImport } from 'helpers/google-drive-import';
import { makeIcloudFetcher, planIcloudImport } from 'helpers/icloud-import';
import { ImportRunResult } from 'helpers/import-types';
import {
  fetchLocalFile,
  LocalPlan,
  planLocalImport,
} from 'helpers/local-folder-import';
import { pickedFolder, pickedFolderName } from 'helpers/picked-folder';
import { classnames, excludeDefaultAlbum } from 'helpers/utils';
import { NotificationObject } from 'models/notificationObject';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { goToPage, RouteName } from 'routes';
import { VaultDetailProvider } from 'context/vault-detail-context';
import { vaultSubPageBreadcrumb } from 'helpers/vault-breadcrumbs';
import { VaultTopBar } from 'pages/vault-detail/components/vault-top-bar';
import { publishMemoryAdded } from 'common/returning-onboarding/onboarding-status';
import styles from './vault-import.module.scss';
import { DropboxPicker } from './components/dropbox-picker';
import { GoogleDriveFilePicker } from './components/google-drive-file-picker';
import { GoogleDrivePicker } from './components/google-drive-picker';
import { IcloudLinkForm } from './components/icloud-link-form';
import { ImportFromSource } from './components/import-from-source';
import { ImportPreview } from './components/import-preview';

type Phase =
  | { kind: 'source' }
  | { kind: 'drive-picker' }
  | { kind: 'drive-preview'; folder: DriveFile }
  | { kind: 'drive-files-preview'; files: DriveFile[] }
  // `from`: the folder the upload dialog's pick came from, if it named one.
  | { kind: 'local-preview'; plan: LocalPlan; from: string | null }
  | { kind: 'dropbox-picker' }
  | { kind: 'dropbox-preview'; folder: DropboxFile }
  | { kind: 'icloud-link' }
  | { kind: 'icloud-preview'; link: string };

// Sources other than iCloud fetch each file independently.
const localFetcher = () => fetchLocalFile;
const driveFetcher = () => fetchDriveFile;
const dropboxFetcher = () => fetchDropboxFile;

// Google's own picker (drive.file) instead of the folder browser, whose
// drive.readonly scope is still waiting on Google's review. Picked files
// arrive unsorted, so there's no folder-to-album mapping.
const DRIVE_VIA_GOOGLE_PICKER = true;

const PICKER_INSTRUCTION =
  'Choose the top-most folder. Each immediate subfolder will become its own ' +
  'album, and any files inside that subfolder (and its subfolders) will ' +
  'become entries of that album. Name each subfolder the way you want the ' +
  'album to be called.';

const VaultImportContent = () => {
  const { id } = useParams<{ id: string }>();
  const vaultId = Number(id);
  const { state: appState, dispatch } = useContext(AppContext);

  // The top bar needs the vault (title, albums for the "New" menu). Reuse the
  // one already in context when it's this vault, and fetch it otherwise.
  const [vault, setVault] = useState<VaultType | undefined>(
    appState.currentVault?.id === vaultId ? appState.currentVault : undefined,
  );
  useEffect(() => {
    if (!(vaultId > 0)) return undefined;
    let cancelled = false;
    VaultsController.getVault(vaultId, 'summary')
      .then((fetched) => {
        if (cancelled) return;
        setVault(fetched);
        dispatch({ type: appActions.SET_CURRENT_VAULT, currentVault: fetched });
      })
      .catch(() => {
        if (!cancelled) goToPage(RouteName.NotFoundRedirect);
      });
    return () => {
      cancelled = true;
    };
  }, [vaultId]);

  // Albums already in the vault, offered as homes for unsorted files. The
  // summary fetch returns albums without contents, so they all land in
  // unansweredAlbums; take both lists to be safe.
  const existingAlbums = useMemo(
    () =>
      excludeDefaultAlbum([
        ...(vault?.albums || []),
        ...(vault?.unansweredAlbums || []),
      ]).map((album) => ({ id: album.id, name: album.name })),
    [vault],
  );

  // Deep link from the empty-vault tiles: ?source=<key> opens that source's
  // step directly. "computer" arrives with the folder already picked
  // (&pick=<token>); after a reload that folder is gone, so it falls back to
  // the source screen.
  const [phase, setPhase] = useState<Phase>(() => {
    const params = new URLSearchParams(window.location.search);
    const source = params.get('source');
    const files =
      source === 'computer' ? pickedFolder(params.get('pick')) : null;
    if (files) {
      return {
        kind: 'local-preview',
        plan: planLocalImport(files),
        from: pickedFolderName(params.get('pick')),
      };
    }
    const preselectable: Record<string, Phase> = {
      drive: { kind: 'drive-picker' },
      dropbox: { kind: 'dropbox-picker' },
      icloud: { kind: 'icloud-link' },
    };
    return (source && preselectable[source]) || { kind: 'source' };
  });
  // A step opened from the vault goes back to the vault, not to this page's
  // source screen the user never saw.
  const [deepLinked] = useState(() => phase.kind !== 'source');

  // Uploading from an album: loose files default to that album, and leaving
  // the review returns to it.
  const [fromAlbumId] = useState(() => {
    const album = Number(
      new URLSearchParams(window.location.search).get('album'),
    );
    return album > 0 ? album : undefined;
  });

  // Uploading to answer a prompt: it's removed once memories are imported.
  const [promptId] = useState(() => {
    const prompt = Number(
      new URLSearchParams(window.location.search).get('prompt'),
    );
    return prompt > 0 ? prompt : undefined;
  });

  const goBackToVault = () => {
    if (fromAlbumId) {
      goToPage(RouteName.AlbumScreen, { id: vaultId, albumId: fromAlbumId });
    } else {
      goToPage(RouteName.VaultDetail, { id: vaultId });
    }
  };

  // Stable, so the pickers' sign-in effects don't re-run on every render.
  const leaveFirstStep = useCallback(() => {
    if (!deepLinked) setPhase({ kind: 'source' });
    else if (fromAlbumId) {
      goToPage(RouteName.AlbumScreen, { id: vaultId, albumId: fromAlbumId });
    } else goToPage(RouteName.VaultDetail, { id: vaultId });
  }, [deepLinked, vaultId, fromAlbumId]);

  const showError = (title: string, message: string) => {
    dispatch({
      type: appActions.NOTIFICATION,
      notification: new NotificationObject({
        show: true,
        title,
        message,
        type: NotificationType.Error,
      }),
    });
  };

  const handleImportDone = (result: ImportRunResult) => {
    dispatch({
      type: appActions.SET_REFETCH_VAULT,
      refetchVault: true,
    });
    if (result.filesCreated > 0) {
      publishMemoryAdded();
      // The prompt stays in the album if it can't be removed.
      if (promptId)
        VaultsController.deleteAlbumContent(promptId).catch(() => {});
    }
    const failedLine =
      result.filesFailed > 0
        ? ` ${result.filesFailed} ${
            result.filesFailed === 1 ? 'file' : 'files'
          } failed.`
        : '';
    dispatch({
      type: appActions.NOTIFICATION,
      notification: new NotificationObject({
        show: true,
        title: 'Import complete',
        message: `Imported ${result.filesCreated} ${
          result.filesCreated === 1 ? 'memory' : 'memories'
        } and created ${result.albumsCreated} ${
          result.albumsCreated === 1 ? 'album' : 'albums'
        }.${failedLine}`,
        type: NotificationType.Success,
      }),
    });
    goBackToVault();
  };

  // Stable plan callbacks so the preview's effect doesn't re-run on every
  // parent render.
  const drivePlanFolder = phase.kind === 'drive-preview' ? phase.folder : null;
  const drivePlan = useCallback(
    () => planDriveImport(drivePlanFolder!),
    [drivePlanFolder],
  );

  const driveFiles = phase.kind === 'drive-files-preview' ? phase.files : null;
  const driveFilesPlan = useCallback(
    () =>
      Promise.resolve({
        rootName: 'Google Drive',
        albums: [],
        unsorted: driveFiles!,
      }),
    [driveFiles],
  );

  const localPlanData = phase.kind === 'local-preview' ? phase.plan : null;
  const localPlan = useCallback(
    () => Promise.resolve(localPlanData!),
    [localPlanData],
  );

  const dropboxPlanFolder =
    phase.kind === 'dropbox-preview' ? phase.folder : null;
  const dropboxPlan = useCallback(
    () => planDropboxImport(dropboxPlanFolder!),
    [dropboxPlanFolder],
  );

  const icloudLink = phase.kind === 'icloud-preview' ? phase.link : null;
  const icloudPlan = useCallback(
    () => planIcloudImport(icloudLink!),
    [icloudLink],
  );

  // The review is a full page of its own (Back and Close in its header).
  const reviewing = phase.kind.endsWith('-preview');

  return (
    <div
      className={classnames(styles.screen, {
        [styles.screenReview]: reviewing,
      })}
    >
      {vault && !reviewing && (
        <VaultTopBar
          vault={vault}
          breadcrumb={vaultSubPageBreadcrumb(vaultId, 'Import')}
        />
      )}
      <div className={styles.page}>
        {phase.kind === 'source' && (
          <ImportFromSource
            onBack={goBackToVault}
            onSelectGoogleDrive={() => setPhase({ kind: 'drive-picker' })}
            onSelectDropbox={() => setPhase({ kind: 'dropbox-picker' })}
            onSelectICloud={() => setPhase({ kind: 'icloud-link' })}
            onSelectLocalFolder={(files) => {
              const plan = planLocalImport(files);
              setPhase({ kind: 'local-preview', plan, from: null });
            }}
          />
        )}

        {phase.kind === 'drive-picker' && DRIVE_VIA_GOOGLE_PICKER && (
          <GoogleDriveFilePicker
            onClose={leaveFirstStep}
            onError={(m) => showError('Google Drive', m)}
            onFilesPicked={(files) =>
              setPhase({ kind: 'drive-files-preview', files })
            }
          />
        )}

        {phase.kind === 'drive-files-preview' && (
          <ImportPreview
            vaultId={vaultId}
            plan={driveFilesPlan}
            makeFetcher={driveFetcher}
            existingAlbums={existingAlbums}
            defaultAlbumId={fromAlbumId}
            scanningLabel={(name) => `Reading '${name}'…`}
            onBack={leaveFirstStep}
            onError={(m) => showError('Google Drive', m)}
            onDone={handleImportDone}
          />
        )}

        {phase.kind === 'drive-picker' && !DRIVE_VIA_GOOGLE_PICKER && (
          <GoogleDrivePicker
            instruction={PICKER_INSTRUCTION}
            onClose={leaveFirstStep}
            onError={(m) => showError('Google Drive', m)}
            onFolderSelected={(folder) =>
              setPhase({ kind: 'drive-preview', folder })
            }
          />
        )}

        {phase.kind === 'drive-preview' && (
          <ImportPreview
            vaultId={vaultId}
            plan={drivePlan}
            makeFetcher={driveFetcher}
            existingAlbums={existingAlbums}
            scanningLabel={(name) => `Scanning '${name}'…`}
            onBack={() => setPhase({ kind: 'drive-picker' })}
            onError={(m) => showError('Google Drive', m)}
            onDone={handleImportDone}
          />
        )}

        {phase.kind === 'dropbox-picker' && (
          <DropboxPicker
            instruction={PICKER_INSTRUCTION}
            onClose={leaveFirstStep}
            onError={(m) => showError('Dropbox', m)}
            onFolderSelected={(folder) =>
              setPhase({ kind: 'dropbox-preview', folder })
            }
          />
        )}

        {phase.kind === 'dropbox-preview' && (
          <ImportPreview
            vaultId={vaultId}
            plan={dropboxPlan}
            makeFetcher={dropboxFetcher}
            existingAlbums={existingAlbums}
            scanningLabel={(name) => `Scanning '${name}'…`}
            onBack={() => setPhase({ kind: 'dropbox-picker' })}
            onError={(m) => showError('Dropbox', m)}
            onDone={handleImportDone}
          />
        )}

        {phase.kind === 'icloud-link' && (
          <IcloudLinkForm
            onBack={leaveFirstStep}
            onSubmit={(link) => setPhase({ kind: 'icloud-preview', link })}
          />
        )}

        {phase.kind === 'icloud-preview' && (
          <ImportPreview
            vaultId={vaultId}
            plan={icloudPlan}
            makeFetcher={makeIcloudFetcher}
            existingAlbums={existingAlbums}
            scanningLabel={(name) => `Fetching '${name}'…`}
            onBack={() => setPhase({ kind: 'icloud-link' })}
            onError={(m) => showError('iCloud', m)}
            onDone={handleImportDone}
          />
        )}

        {phase.kind === 'local-preview' && (
          <ImportPreview
            vaultId={vaultId}
            plan={localPlan}
            makeFetcher={localFetcher}
            existingAlbums={existingAlbums}
            defaultAlbumId={fromAlbumId}
            sourceName={phase.from}
            scanningLabel={(name) => `Reading '${name}'…`}
            onBack={leaveFirstStep}
            onError={(m) => showError('Upload', m)}
            onDone={handleImportDone}
          />
        )}
      </div>
    </div>
  );
};

// The top bar's search / "New" menu need the vault detail context. Keyed by
// the query, so an Upload picked from this page's own top bar starts afresh.
export const VaultImport = () => {
  const { search } = useLocation();
  return (
    <VaultDetailProvider>
      <VaultImportContent key={search} />
    </VaultDetailProvider>
  );
};
